import React, { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import MembersTable from "./components/membersTable"
import { toast } from "react-toastify"

import { editTeamMember, fetchTeams, inviteUserToTeam, removeTeamMember } from "../../../actions/team"
import Breadcrumb from "../../../components/breadcrumb"
import type { Destination, Team, TeamMember } from "../../../constants/types"
import { getRequest } from "../../../helpers"
import type { AppDispatch, RootState } from "../../../store"
import type { EditForm } from "./components/editMemberModal"
import { EditMemberModal } from "./components/editMemberModal"
import type { InviteForm } from "./components/inviteMemberModal"
import { InviteMemberModal } from "./components/inviteMemberModal"

const breadcrumbs = [
  { name: "account.team.breadcrumb.home", url: "/handbooks" },
  { name: "account.team.breadcrumb.settings" }
]

const TeamMembersContainer: React.FC = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch<AppDispatch>()

  const teams = useSelector((state: RootState) => state.team.teams) as Team[]
  const destinations = useSelector((state: RootState) => state.destinations.destinations) as Destination[]

  const [selectedTeam, setSelectedTeam] = useState<Team | undefined>()
  const [selectedTeamMembers, setSelectedTeamMembers] = useState<TeamMember[]>([])
  const [selectedTeamMember, setSelectedTeamMember] = useState<TeamMember | undefined>()
  const [inviteOpen, setInviteOpen] = useState(false)
  const [editOpen, setEditOpen] = useState(false)

  const teamDestinations = useMemo(
    () => destinations.filter(d => d.ownerTeamId === selectedTeam?.id),
    [destinations, selectedTeam]
  )

  const fetchTeamMembers = async () => {
    try {
      const response = (await getRequest({
        url: `/api/v1/profile/team/${selectedTeam?.id}/members`,
        auth: true
      })) as TeamMember[]
      setSelectedTeamMembers(response)
    } catch (error) {
      setSelectedTeamMembers([])
    }
  }

  useEffect(() => {
    dispatch(fetchTeams())
  }, [dispatch])

  useEffect(() => {
    if (selectedTeam) {
      fetchTeamMembers()
    } else {
      setSelectedTeamMembers([])
    }
  }, [selectedTeam])

  useEffect(() => {
    if (!selectedTeam) {
      setSelectedTeam(teams[0])
    }
  }, [teams])

  const selectTeam = (id: string) => {
    const selected = teams.find(x => x.id === id)
    setSelectedTeam(selected)
  }

  const handleInviteSubmit = async (values: InviteForm) => {
    if (selectedTeam) {
      try {
        await dispatch(inviteUserToTeam(selectedTeam.id, values))

        toast.success(t("auth.notifications.team.userInvited", { email: values.email }))
        await fetchTeamMembers()
      } catch (err) {
        toast.error(t("auth.notifications.team.userInviteFailed"))
      }
    }
  }

  const handleRemoveUser = async (teamUser: TeamMember) => {
    if (!selectedTeam || !teamUser.id) return
    try {
      await dispatch(removeTeamMember(selectedTeam.id, teamUser.id))

      toast.success(t("auth.notifications.team.userRemoved", { email: teamUser.email ?? teamUser.invitedEmail }))
      await fetchTeamMembers()
    } catch (err) {
      toast.error(t("auth.notifications.team.removeFailed"))
    }
  }

  const handleEditUser = async ({
    teamUserId,
    role,
    destinations
  }: { teamUserId: string } & Pick<EditForm, "role" | "destinations">) => {
    if (!selectedTeam || !teamUserId) return
    try {
      await dispatch(editTeamMember(selectedTeam.id, teamUserId, { role, destinations }))

      toast.success(t("auth.notifications.team.userEdited"))
      await fetchTeamMembers()
    } catch (err) {
      toast.error(t("auth.notifications.team.editFailed"))
    }
  }

  return (
    <>
      <Breadcrumb data={breadcrumbs} />
      <div className="tw-mb-6 tw-flex tw-items-center tw-justify-between">
        <h1 className="tw-font-display tw-text-4xl tw-font-semibold">{t("account.team.title")}</h1>
        <div className="tw-flex tw-items-center tw-justify-between">
          {/* {(userRoleInSelectedTeam === "account_holder" || userRoleInSelectedTeam === "admin") && (
            <button
              type="button"
              onClick={() => {}}
              className="tw-inline-flex tw-items-center tw-justify-center tw-rounded-xl tw-bg-primary 
          tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-white tw-shadow-sm tw-mr-4
          hover:tw-bg-primary-dark focus:tw-outline-none focus:tw-ring-2 focus:tw-primary-dark">
              Create team
            </button>
          )} */}
          <label className="tw-sr-only" htmlFor="team">
            Team
          </label>
          <select
            id="team"
            value={selectedTeam?.id}
            onChange={e => selectTeam(e.target.value)}
            className="tw-inline-flex tw-h-10 tw-min-w-60 tw-rounded-xl tw-border tw-border-gray-200 tw-bg-white tw-px-3
              tw-text-sm tw-text-dark tw-shadow-sm focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-dark-50">
            {teams.map(ws => (
              <option key={ws.id} value={ws.id}>
                {ws.name}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="tw-mb-12 tw-rounded tw-bg-white tw-shadow">
        {/* Basic information */}
        <div className="tw-w-full tw-border-b tw-p-12">
          <h4 className="tw-mb-8">{t("account.team.basicInfo")}</h4>
          <p>
            <b className="tw-pr-2">{t("account.team.form.name")}:</b>
            {selectedTeam?.name}
          </p>
          {selectedTeam?.isBusiness && (
            <div>
              <p>
                <b className="tw-pr-2">{t("account.team.form.company")}:</b>
                {selectedTeam?.businessName} ({selectedTeam?.businessId})
              </p>
              <p>
                <b className="tw-pr-2">{t("account.team.form.phone")}:</b>
                {selectedTeam?.contactPhone}
              </p>
            </div>
          )}
          {/* {(userRoleInSelectedTeam === "account_holder" || userRoleInSelectedTeam === "admin") && (
            <>
              <div className="tw-flex tw-space-x-4">
                <button
                  type="button"
                  className="tw-btn tw-btn-primary tw-my-4 tw-flex tw-h-8 tw-min-w-[20px] tw-items-center tw-justify-start">
                  <span>{t("account.team.button.edit")}</span>
                </button>
              </div>
            </>
          )} */}
        </div>
        {/* Members */}
        <div className="tw-w-full tw-p-12">
          <MembersTable
            members={selectedTeamMembers}
            displayActions={
              (selectedTeam?.userRole === "account_holder" || selectedTeam?.userRole === "admin") ?? false
            }
            onAddUser={() => setInviteOpen(true)}
            onAction={(action, teamUser) => {
              if (action === "remove") {
                handleRemoveUser(teamUser)
              } else if (action === "edit") {
                setSelectedTeamMember(teamUser)
                setEditOpen(true)
              }
            }}
          />
        </div>
      </div>

      <InviteMemberModal
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        onSubmit={handleInviteSubmit}
        destinations={teamDestinations}
        selectedTeam={selectedTeam}
      />

      <EditMemberModal
        open={editOpen}
        onClose={() => setEditOpen(false)}
        onSubmit={handleEditUser}
        destinations={teamDestinations}
        member={selectedTeamMember}
        selectedTeam={selectedTeam}
      />
    </>
  )
}

export default TeamMembersContainer
